import React from 'react';
import { X, ChevronRight, CloudDownload } from 'lucide-react';

interface UpdateModalPreviewProps {
  title: string;
  features: string[];
  ctaLabel?: string;
}

/**
 * Live preview of the creator app's "What's New" update modal.
 * Mirrors the design of creator-app/components/AppUpdateModal.tsx
 * (dark surfaces, pink accent, bottom sheet layout) inside a phone frame.
 */
export default function UpdateModalPreview({
  title,
  features,
  ctaLabel,
}: UpdateModalPreviewProps) {
  const hasContent = title.trim() || features.some((f) => f.trim()) || ctaLabel?.trim();

  return (
    <div className="w-[375px] mx-auto bg-white rounded-[2rem] shadow-xl border-[8px] border-gray-800 overflow-hidden flex flex-col" style={{ height: 812 }}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 py-2 text-xs font-semibold text-gray-800">
        <span>1:00</span>
        <div className="flex items-center gap-1">
          <span>●●●</span>
        </div>
      </div>

      {/* App screen — dark background */}
      <div className="flex-1 relative overflow-hidden" style={{ background: '#0A0A0A' }}>
        {/* Backdrop */}
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.7)' }} />

        {/* Bottom sheet */}
        <div
          className="absolute inset-x-0 bottom-0 flex flex-col items-center"
          style={{
            background: '#141414',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingTop: 24,
            paddingLeft: 24,
            paddingRight: 24,
            paddingBottom: 40,
            maxHeight: '80%',
          }}
        >
          {/* Close button */}
          <div
            className="absolute top-4 right-4 flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 16, background: '#1E1E1E' }}
          >
            <X size={20} color="rgba(255,255,255,0.38)" />
          </div>

          {/* Header */}
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚀</div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: '#FFFFFF',
              textAlign: 'center',
              marginBottom: 4,
            }}
          >
            {title.trim() || 'What\'s New in v2.1.0'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.55)',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            Here's what's new in this version
          </div>

          {/* Feature list */}
          <div className="w-full overflow-y-auto no-scrollbar" style={{ maxHeight: 240 }}>
            <div className="flex flex-col" style={{ gap: 8, paddingBottom: 4 }}>
              {features.length === 0 && !hasContent ? (
                <div
                  className="flex items-center gap-2.5"
                  style={{
                    background: '#1E1E1E',
                    borderRadius: 12,
                    padding: '12px 14px',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div style={{ width: 7, height: 7, borderRadius: 4, background: '#E91E8C' }} />
                  <span style={{ flex: 1, fontSize: 14, color: 'rgba(255,255,255,0.38)', fontWeight: 500 }}>
                    Features will appear here...
                  </span>
                </div>
              ) : (
                features.map((feature, i) => (
                  <div
                    key={i}
                    className="flex items-center"
                    style={{
                      gap: 10,
                      background: '#1E1E1E',
                      borderRadius: 12,
                      padding: '12px 14px',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <div style={{ width: 7, height: 7, borderRadius: 4, background: '#E91E8C', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14, color: '#FFFFFF', fontWeight: 500 }}>
                      {feature.trim() || `Feature ${i + 1}`}
                    </span>
                    <ChevronRight size={14} color="rgba(255,255,255,0.38)" />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* CTA */}
          <div
            className="flex items-center justify-center w-full"
            style={{
              gap: 8,
              borderRadius: 16,
              background: '#E91E8C',
              padding: '16px 0',
              marginTop: 20,
              boxShadow: '0 6px 12px rgba(233,30,140,0.35)',
            }}
          >
            <CloudDownload size={18} color="#FFFFFF" />
            <span style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF' }}>
              {ctaLabel?.trim() || 'Update Now'}
            </span>
          </div>

          {/* Skip */}
          <div style={{ marginTop: 14, padding: '4px 0' }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)' }}>Maybe later</span>
          </div>
        </div>
      </div>
    </div>
  );
}